import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Sparkle, Languages, MessageCircle, LogIn } from "lucide-react";
import { LOGO_URL, scrollToSection } from "../config";
import { useSite, waLink } from "../siteContext";
import { useLang } from "../i18n";

export const Header = () => {
  const [open, setOpen] = useState(false);
  const { lang, setLang, s } = useLang();
  const settings = useSite();

  const LINKS = [
    { id: "about", label: s.nav_about },
    { id: "courses", label: s.nav_courses },
    { id: "consultations", label: s.nav_consult },
    { id: "journey", label: s.nav_journey },
    { id: "testimonials", label: s.nav_testimonials },
    { id: "blog", label: s.nav_blog },
    { id: "corporate", label: s.nav_corporate },
    { id: "faq", label: s.nav_faq },
  ];
  const hiddenSections = settings.hidden_sections || [];
  const hiddenEls = settings.hidden_elements || [];
  const headerCustomBtns = (settings.custom_buttons || []).filter((b) => b.location === "header");
  const visibleLinks = LINKS.filter((l) => l.id === "blog" || l.id === "corporate" || !hiddenSections.includes(l.id));

  const go = (id) => {
    setOpen(false);
    if (id === "corporate") {
      window.location.href = "/corporate";
      return;
    }
    if (id === "blog") {
      window.location.href = "/p/morning-routine";
      return;
    }
    scrollToSection(id);
  };

  return (
    <header
      data-testid="site-header"
      className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-luxury-black/85 border-b border-luxury-border"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 h-20 flex items-center justify-between">
        <button
          data-testid="header-logo"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center gap-3"
          aria-label="TheMaya الرئيسية"
        >
          <img src={LOGO_URL} alt="TheMaya" className="h-9 w-auto" />
        </button>

        <nav className="hidden lg:flex items-center gap-6 xl:gap-8" data-testid="header-nav">
          {visibleLinks.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-${l.id}`}
              onClick={() => go(l.id)}
              className="text-[13px] xl:text-sm whitespace-nowrap text-luxury-gray hover:text-luxury-gold transition-colors duration-300 tracking-wide"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          {headerCustomBtns.map((b) => (
            <a
              key={b.id}
              data-testid={`header-custom-btn-${b.id}`}
              href={b.url}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden lg:inline-flex text-sm text-luxury-gray hover:text-luxury-gold transition-colors duration-300 tracking-wide"
            >
              {b.label}
            </a>
          ))}
          {!hiddenEls.includes("header_login") && (
          <Link
            to="/account"
            data-testid="header-login-button"
            className="hidden md:inline-flex items-center gap-2 text-sm text-luxury-bone border border-luxury-border px-5 py-2.5 rounded-full hover:border-luxury-gold hover:text-luxury-gold transition-colors duration-300"
          >
            <LogIn className="w-4 h-4" strokeWidth={1.5} />
            {lang === "ar" ? "تسجيل الدخول" : "Login"}
          </Link>
          )}
          {!hiddenEls.includes("header_whatsapp") && (
          <a
            data-testid="header-whatsapp-button"
            href={waLink(settings, undefined, lang)}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={s.wa_aria}
            className="inline-flex items-center gap-2 bg-luxury-surface border border-luxury-gold/50 text-luxury-gold text-sm font-semibold px-5 py-2.5 rounded-full hover:bg-luxury-gold hover:text-luxury-black transition-colors duration-300"
          >
            <MessageCircle className="w-4 h-4" strokeWidth={1.75} />
            <span className="hidden sm:inline">{s.hero_whatsapp}</span>
          </a>
          )}
          <button
            data-testid="lang-toggle"
            onClick={() => setLang(lang === "ar" ? "en" : "ar")}
            className="flex items-center gap-1.5 border border-luxury-border text-luxury-bone text-sm px-4 py-2 rounded-full hover:border-luxury-gold hover:text-luxury-gold transition-colors duration-300"
            aria-label="Language"
          >
            <Languages className="w-4 h-4" strokeWidth={1.5} />
            {lang === "ar" ? "EN" : "عربي"}
          </button>
          {!hiddenEls.includes("header_cta") && (
          <button
            data-testid="header-cta-button"
            onClick={() => go("contact")}
            className="hidden sm:inline-flex items-center gap-2 bg-luxury-gold text-luxury-black text-sm font-semibold px-6 py-2.5 rounded-full hover:bg-luxury-goldLight transition-colors duration-300"
          >
            <Sparkle className="w-4 h-4" strokeWidth={1.5} />
            {s.cta_start}
          </button>
          )}
          <button
            data-testid="mobile-menu-button"
            onClick={() => setOpen(!open)}
            className="lg:hidden text-luxury-bone p-2"
            aria-label="القائمة"
          >
            {open ? <X className="w-6 h-6" strokeWidth={1.5} /> : <Menu className="w-6 h-6" strokeWidth={1.5} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden overflow-hidden border-t border-luxury-border bg-luxury-black/95"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {visibleLinks.map((l) => (
                <button
                  key={l.id}
                  data-testid={`mobile-nav-${l.id}`}
                  onClick={() => go(l.id)}
                  className="text-start text-lg text-luxury-bone hover:text-luxury-gold transition-colors"
                >
                  {l.label}
                </button>
              ))}
              {headerCustomBtns.map((b) => (
                <a
                  key={b.id}
                  data-testid={`mobile-custom-btn-${b.id}`}
                  href={b.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-start text-lg text-luxury-bone hover:text-luxury-gold transition-colors"
                >
                  {b.label}
                </a>
              ))}
              {!hiddenEls.includes("header_login") && (
              <Link
                to="/account"
                data-testid="mobile-login-button"
                onClick={() => setOpen(false)}
                className="mt-2 inline-flex items-center justify-center gap-2 border border-luxury-gold/60 text-luxury-gold font-semibold px-6 py-3 rounded-full hover:bg-luxury-gold hover:text-luxury-black transition-colors duration-300"
              >
                <LogIn className="w-4 h-4" strokeWidth={1.5} />
                {lang === "ar" ? "تسجيل الدخول" : "Login"}
              </Link>
              )}
              {!hiddenEls.includes("header_whatsapp") && (
              <a
                data-testid="mobile-whatsapp-button"
                href={waLink(settings, undefined, lang)}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 inline-flex items-center justify-center gap-2 bg-luxury-surface border border-luxury-gold/50 text-luxury-gold font-semibold px-6 py-3 rounded-full hover:bg-luxury-gold hover:text-luxury-black transition-colors duration-300"
              >
                <MessageCircle className="w-4 h-4" strokeWidth={1.75} />
                {s.hero_whatsapp}
              </a>
              )}
              {!hiddenEls.includes("header_cta") && (
              <button
                data-testid="mobile-cta-button"
                onClick={() => go("contact")}
                className="bg-luxury-gold text-luxury-black font-semibold px-6 py-3 rounded-full"
              >
                {s.cta_start}
              </button>
              )}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
